import React, { Fragment, Suspense, memo, useRef, useCallback, useEffect, useMemo } from "react";

import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";
import AxiomLoader from "__AxiomComponents/AxiomUI/AxiomLoader/AxiomLoader.jsx";
import { useWidthHeightCalculator } from "__AxiomComponents/AxiomHooks/useWidthHeightCalculator";
import {
  useIsVisible,
  useDelayedRendering,
  getToolTipData,
  useDownloadData,
} from "./HawkeyeWidgetHooks";
import { renderWidget } from "./widgetToAppMapping";

import HawkeyeV2Insights from "../HawkeyeV2Insights/HawkeyeV2Insights";
import HawkeyeV2Filters from "../HawkeyeV2Filters/HawkeyeV2Filters";
import HawkeyeCommunication from "../HawkeyeCommunication/HawkeyeCommunication";
import HawkeyeV2DataDownload from "../HawkeyeV2DataDownload/HawkeyeV2DataDownload";
import HawkeyeV2DataViewer from "../HawkeyeV2DataDownload/HawkeyeV2DataViewer";
import AxiomErrorBoundry from "__AxiomComponents/AxiomErrorBoundry/AxiomErrorBoundry";
import Icon from "../Icons/Icon";
import AxiomModal from "__AxiomComponents/AxiomModal/AxiomModal";
import Lineage from "../../../Platform/components/LineageWrapperComponent/LineageWrapperComponent";
import { useDownloadPDF } from "__AxiomComponents/AxiomHooks/AxiomWidgetHooks";
import parse from "html-react-parser";
import * as d3 from "d3";
import "./HawkeyeV2Widget.scss";

function HawkeyeV2Widget(props) {
  const {
    widget,
    KPIWiseConfigs,
    selectedGlobalFilter,
    currentPage,
    setGlobalFilters,
    HawkeyeV2AppSettings = {},
    dashboardDate = new Date(),
  } = props;

  const [state, dispatch] = useAxiomState(
    {
      KPIWiseFilters: null,
      viewDetails: null,
      userDefinedSettings: null,
      infoTooltipVisible: false,
      tooltipData: null,
      isExpanded: false,
      isDownloadDataOpen: false,
      isRawDataDisplayModalOpen: false,
      rawTableData: null,
      downloadFileName: null,
      downloadFolderName: null,
      downloadFolderPath: null,
      shouldZipDownloadData: false,
      KPIWiseLocalFilters: null,
      loading: false,
    },
    props.appName,
    props.appID,
    "HawkeyeV2Widget",
    props.componentID
  );

  const { KPIWiseFilters } = state;

  const [contentRef, widgetWidth] = useWidthHeightCalculator();

  const widgetRef = useRef();

  const isVisible = useIsVisible(widgetRef);
  const shouldRender = useDelayedRendering(250);

  const shouldRenderWidget = isVisible && shouldRender && widgetWidth && KPIWiseConfigs;

  const widgetHeightRatio = widget.widgetSettings.settings.heightByWidthRatio || 0.4;
  const widgetHeight = widgetWidth * widgetHeightRatio;
  const widgetContentStyles = { height: widgetHeight };

  const downloadPDF = useDownloadPDF(
    widgetRef,
    state.isExpanded,
    HawkeyeV2AppSettings["is-two-column-view"]
  );

  const setWidgetLocalFilters = useCallback(
    (filter) => {
      dispatch({ KPIWiseLocalFilters: filter });
    },
    [dispatch]
  );

  const showInfoTooltip = useCallback(() => {
    let tooltipData;
    if (typeof widget.widgetInformation === "string") tooltipData = parse(widget.widgetInformation);
    else if (typeof widget.widgetInformation === "object") {
      tooltipData = getToolTipData(widget.widgetInformation);
    }
    dispatch({ infoTooltipVisible: true, tooltipData });
  }, [dispatch, widget.widgetInformation]);

  const hideInfoTooltip = useCallback(() => {
    dispatch({ infoTooltipVisible: false });
  }, [dispatch]);

  const setKPIWiseFilters = useCallback((KPIWiseFilters) => dispatch({ KPIWiseFilters }), [
    dispatch,
  ]);

  const onSaveUserDefinedSettings = useCallback(
    (userDefinedSettings) => {
      dispatch(
        {
          widgetSubscribedToUserID: widget.widgetID,
          userDefinedSettings,
        },
        "AxiomEvent",
        "saveUserDefinedSettings"
      );
    },
    [dispatch, widget.widgetID]
  );

  // Update user settings to the state
  useEffect(() => {
    if (widget && widget.userDefinedSettings) {
      dispatch({
        userDefinedSettings: widget.userDefinedSettings,
      });
    }
  }, [widget, dispatch]);

  const onExpandToggleView = useCallback(() => {
    if (!state.isExpanded) {
      document.documentElement.classList.add("widget-is-expanded");
      document.body.classList.add("widget-expanded");
    } else {
      document.documentElement.classList.remove("widget-is-expanded");
      document.body.classList.remove("widget-expanded");
    }
    dispatch(
      {
        isExpanded: !state.isExpanded,
      },
      "AxiomEventInfo",
      "toggleExpandWidget"
    );
  }, [dispatch, state.isExpanded]);

  const openLineageModal = useCallback(() => {
    dispatch({ showLineage: true, lineageId: widget.subscribeID });
  }, [dispatch, widget.subscribeID]);

  const closeLineageModal = useCallback(() => {
    dispatch({ showLineage: false, lineageId: null });
  }, [dispatch]);

  const closeDownloadData = useCallback(() => {
    dispatch({ isDownloadDataOpen: false });
  }, [dispatch]);

  const openDownloadData = useCallback(() => {
    dispatch({
      isDownloadDataOpen: true,
      ...(!state.viewDetails ? { loading: true } : null),
    });
    // If view Details are not available in the current state fetch it
    if (!state.viewDetails) {
      dispatch(
        {
          KPIList: widget.widgetSettings.KPIList,
        },
        "AxiomEvent",
        "getViewDetails"
      );
    }
  }, [dispatch, state.viewDetails, widget.widgetSettings.KPIList]);

  const closeDataViewer = useCallback(() => {
    dispatch({
      isRawDataDisplayModalOpen: false,
      rawTableData: null,
    });
  }, [dispatch]);

  const downloadOptions = useDownloadData(
    state.viewDetails,
    widget.widgetSettings.KPIList,
    KPIWiseConfigs
  );

  // Download the data files once the information is received from server
  useEffect(() => {
    if (state.downloadFileName && !state.shouldZipDownloadData) {
      window.open(
        `${AxiomAPIs.URL}/downloadFile/export/${encodeURIComponent(
          state.downloadFolderName + "/" + state.downloadFileName
        )}`
      );
    }
    if (state.downloadFileName && state.shouldZipDownloadData) {
      window.open(
        `/zipFolderAndDownload/${encodeURIComponent(state.downloadFolderPath)}/${encodeURIComponent(
          state.downloadFolderName
        )}`,
        "_blank"
      );
    }
    dispatch({
      downloadFileName: null,
      downloadFolderName: null,
      downloadFolderPath: null,
      shouldZipDownloadData: false,
    });
  }, [
    dispatch,
    state.downloadFileName,
    state.downloadFolderName,
    state.downloadFolderPath,
    state.shouldZipDownloadData,
  ]);

  const onDownloadData = useCallback(
    (selectedViewDetails, KPIWiseFilters, KPIWiseLocalFilters, optionalDisplayLimit = 0) => {
      dispatch({ loading: true });
      dispatch(
        {
          selectedViewDetails: selectedViewDetails,
          KPIWiseFilters: KPIWiseFilters,
          KPIWiseLocalFilters: KPIWiseLocalFilters,
          displayResultRowLimit: optionalDisplayLimit,
          maxRowDownloadLimit: HawkeyeV2AppSettings.maxDownloadLimit || 100000,
          zipDownloadLimit: HawkeyeV2AppSettings.zipDownloadLimit || 10000,
          KPIList: widget.widgetSettings.KPIList,
        },
        "AxiomEvent",
        "downloadData"
      );
    },
    [dispatch, HawkeyeV2AppSettings, widget.widgetSettings.KPIList]
  );

  return (
    <div>
      <div
        ref={widgetRef}
        data-widget-id={widget.widgetID}
        className={"hawkeye-card hawkeye-widget" + (state.isExpanded ? " is-expanded" : " ")}
        id={widget.widgetID}
      >
        <div className="hawkeye-widget-heading">
          <h2 className="hawkeye-widget-title">{AxiomAPIs.translate(widget.widgetquestion)}</h2>
          <div className="hawkeye-widget-actions">
            {state.infoTooltipVisible && (
              <div
                className="hawkeye-widget-info"
                onMouseEnter={showInfoTooltip}
                onMouseLeave={hideInfoTooltip}
              >
                <div className="hawkeye-widget-info-data"> {state.tooltipData}</div>
              </div>
            )}

            <div
              className="hawkeye-widget-action"
              onMouseEnter={showInfoTooltip}
              onMouseLeave={hideInfoTooltip}
            >
              <Icon icon="ICON_INFO" className="hawkeye-widget-action-icon" />
            </div>

            {selectedGlobalFilter != null && (
              <div className="hawkeye-widget-action">
                <Icon icon="ICON_GLOBAL" className="hawkeye-widget-action-icon" />
                <div className="hawkeye-widget-action-tooltip">Global Filters Applied</div>
              </div>
            )}

            {HawkeyeV2AppSettings && HawkeyeV2AppSettings.isDownloadData != false && (
              <button
                aria-label={"download"}
                onClick={openDownloadData}
                className="hawkeye-action hawkeye-widget-action"
              >
                <Icon className="hawkeye-widget-action-icon" icon={"ICON_DOWNLOAD"} />
                <div className="hawkeye-widget-action-tooltip">Download Data</div>
              </button>
            )}

            {HawkeyeV2AppSettings && HawkeyeV2AppSettings.isWidgetScreenshotCapture && (
              <button
                aria-label={"capture screen"}
                onClick={() => downloadPDF(widget.widgetquestion)}
                className="hawkeye-action hawkeye-widget-action"
              >
                <Icon className="hawkeye-widget-action-icon" icon="ICON_SCREEN_CAPTURE" />
                <div className="hawkeye-widget-action-tooltip">Capture Screenshot</div>
              </button>
            )}
            {HawkeyeV2AppSettings && HawkeyeV2AppSettings.hideDataLineage != true && (
              <button
                aria-label={"show lineage"}
                onClick={openLineageModal}
                className="hawkeye-action hawkeye-widget-action"
              >
                <Icon className="hawkeye-widget-action-icon" icon={"ICON_COLLABORATORS"} />
                <div className="hawkeye-widget-action-tooltip">Data Lineage</div>
              </button>
            )}

            <button
              aria-label={state.isExpanded ? "collapse" : "expand"}
              onClick={onExpandToggleView}
              className="hawkeye-action hawkeye-widget-action"
            >
              <Icon
                className="hawkeye-widget-action-icon"
                icon={state.isExpanded ? "ICON_FULLSCREEN_EXIT" : "ICON_FULLSCREEN"}
              />
              <div className="hawkeye-widget-action-tooltip">
                {state.isExpanded ? "Exit Projection Mode" : "Enter Projection Mode"}
              </div>
            </button>
          </div>
        </div>

        <HawkeyeV2Filters
          widget={widget}
          setKPIWiseFilters={setKPIWiseFilters}
          KPIWiseConfigs={KPIWiseConfigs}
          appName={props.appName}
          appID={props.appID}
          componentID={props.componentID}
          selectedGlobalFilter={selectedGlobalFilter}
          currentPage={currentPage}
          setGlobalFilters={setGlobalFilters}
          isRightSideFilterPanelRequired={HawkeyeV2AppSettings.isRightSideFilterPanelRequired}
          showPublicToggle={HawkeyeV2AppSettings.showPublicToggle}
        />
        <HawkeyeV2Insights
          widget={widget}
          appName={props.appName}
          appID={props.appID}
          componentID={props.componentID}
        />
        <div ref={contentRef} style={widgetContentStyles} className="hawkeye-widget-content">
          {!shouldRenderWidget && (
            <AxiomLoader background={"rgba(255, 255, 255, 0.4)"} block=".hawkeye-widget-content" />
          )}
          <Suspense fallback={null}>
            <AxiomErrorBoundry>
              {shouldRenderWidget &&
                renderWidget(
                  widget,
                  widgetWidth,
                  widgetHeight,
                  KPIWiseConfigs,
                  state.viewDetails,
                  KPIWiseFilters,
                  d3.timeFormat("%d-%b-%Y 23:59:59")(new Date(dashboardDate)), //dashboardDate
                  state.userDefinedSettings,
                  onSaveUserDefinedSettings,
                  setWidgetLocalFilters,
                  onDownloadData
                )}
            </AxiomErrorBoundry>
          </Suspense>
        </div>
      </div>
      <div className="hawkeye-card hawkeye-widget-conversations">
        {widget.projectID != null && shouldRenderWidget && (
          <HawkeyeCommunication
            widget={widget}
            appName={props.appName}
            appID={props.appID}
            componentID={props.componentID}
          />
        )}
      </div>

      {state.isDownloadDataOpen && (
        <AxiomErrorBoundry>
          <HawkeyeV2DataDownload
            downloadOptions={downloadOptions}
            onDownloadData={onDownloadData}
            appName={props.appName}
            appID={props.appID}
            componentID={props.componentID}
            closeDownloadData={closeDownloadData}
            KPIWiseFilters={KPIWiseFilters}
            KPIWiseLocalFilters={state.KPIWiseLocalFilters}
            userDefinedSettings={state.userDefinedSettings}
            onSaveUserDefinedSettings={onSaveUserDefinedSettings}
          />
        </AxiomErrorBoundry>
      )}

      {state.isRawDataDisplayModalOpen && (
        <AxiomErrorBoundry>
          <HawkeyeV2DataViewer data={state.rawTableData} onClose={closeDataViewer} />
        </AxiomErrorBoundry>
      )}

      {state.showLineage && (
        <AxiomModal isOpen={true} onClose={closeLineageModal}>
          <AxiomErrorBoundry>
            <Lineage
              appName={"Platform"}
              appID={0}
              componentID={0}
              showOnlySelected={true}
              selectedNode={{ type: "widget", id: state.lineageId }}
            />
          </AxiomErrorBoundry>
        </AxiomModal>
      )}

      {state.loading && (
        <AxiomLoader background={"rgba(255, 255, 255, 0.4)"} fullScreen={true} block=".axiom-app" />
      )}
    </div>
  );
}

export default memo(HawkeyeV2Widget);
